import { Box, CircularProgress, InputBase, Stack, Typography } from '@mui/material';
import {
  ChevronRight,
  X as CloseIcon,
  LayoutGrid,
  List,
  Plus,
  Search,
  Settings,
  Upload,
  Users,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useNavigate } from 'react-router-dom';
import FileInspector from '../components/Projects/FileInspector.jsx';
import FolderTree from '../components/Projects/FolderTree.jsx';
import MembersModal from '../components/Projects/MembersModal.jsx';
import ProjectCard from '../components/Projects/ProjectCard.jsx';
import ProjectSettingsModal from '../components/Projects/ProjectSettingsModal.jsx';
import SyncControls from '../components/Projects/SyncControls.jsx';
import { ButtonPush } from '../components/ui/ButtonPush.jsx';
import { ButtonStd } from '../components/ui/ButtonStd.jsx';
import CardSection from '../components/ui/CardSection.jsx';
import CardTitle from '../components/ui/CardTitle.jsx';
import ConfirmDialog from '../components/ui/ConfirmDialog.jsx';
import HandleTop from '../components/ui/HandleTop.jsx';
import InputStd from '../components/ui/InputStd.jsx';
import ScrollBox from '../components/ui/ScrollBox.jsx';
import WindowBackground from '../components/ui/WindowBackground.jsx';
import { useAppMode } from '../hooks/useAppMode.js';
import { useAuthStore } from '../stores/authStore.js';
import { useProjectStore } from '../stores/projectStore.js';

const ProjectExplorer = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const {
    projects,
    currentProjectId,
    projectFiles,
    projectMembers,
    syncStatus,
    starredProjectIds,
    isLoading,
    error,
    fetchProjects,
    createProject,
    deleteProject,
    updateProject,
    setCurrentProject,
    uploadFile,
    deleteFile,
    addMember,
    removeMember,
    triggerSync,
    fetchSyncStatus,
    toggleStarred,
    clearError,
  } = useProjectStore();

  // UI state
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDesc, setNewProjectDesc] = useState('');
  const [showMembers, setShowMembers] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // Fetch projects on mount and whenever we return to the list view
  useEffect(() => {
    if (!currentProjectId) {
      fetchProjects();
    }
  }, [currentProjectId, fetchProjects]);

  const currentProject = projects.find((p) => p.project_id === currentProjectId);

  // Determine ownership by comparing creator_username with current Keycloak user
  const currentUser = useAuthStore((state) => state.user);
  const currentUsername = currentUser?.preferred_username || '';
  const isOwner = currentProject?.creator_username === currentUsername;

  // Runtime mode (online vs standalone, desktop vs browser)
  const { canSync, canManageMembers, canShowFileDiff, canShowDirtyBadge } = useAppMode();

  // Filtered projects
  const starredSet = new Set(starredProjectIds);
  const filteredProjects = projects.filter((p) => {
    if (filter === 'starred' && !starredSet.has(p.project_id)) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!p.name.toLowerCase().includes(q) && !(p.description || '').toLowerCase().includes(q))
        return false;
    }
    return true;
  });

  // Handlers
  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;
    try {
      const project = await createProject(newProjectName.trim(), newProjectDesc.trim() || null);
      setNewProjectName('');
      setNewProjectDesc('');
      setShowCreateForm(false);
      await setCurrentProject(project.project_id);
    } catch {}
  };

  // Clear selected file when switching projects
  useEffect(() => {
    setSelectedFile(null);
  }, [currentProjectId]);

  // Auto-select first file when project files load
  useEffect(() => {
    if (projectFiles.length > 0 && !selectedFile) {
      setSelectedFile(projectFiles[0]);
    }
  }, [projectFiles]);

  const handleOpenProject = (project) => {
    setCurrentProject(project.project_id);
    setSelectedFile(null);
  };

  const handleBack = () => {
    setCurrentProject(null);
    setSelectedFile(null);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    for (const file of files) {
      try {
        setUploadProgress({ name: file.name, progress: 0 });
        await uploadFile(file, (progress) => setUploadProgress({ name: file.name, progress }));
      } catch {}
    }
    setUploadProgress(null);
    if (e.target) e.target.value = '';
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await triggerSync();
    } finally {
      setSyncing(false);
    }
  };

  const handleOpenFile = (file) => {
    navigate('/analysis', { state: { projectId: currentProjectId, filename: file.filename } });
  };

  const handleDeleteFile = async (file) => {
    try {
      await deleteFile(file.filename);
      setSelectedFile(null);
    } catch {}
  };

  const handleDeleteProject = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteProject(deleteConfirm.id);
    } finally {
      setDeleteConfirm(null);
    }
  };

  // ── PROJECT LIST VIEW ──────────────────────────────────────────────

  if (!currentProjectId) {
    return (
      <>
        <input
          ref={fileInputRef}
          type="file"
          accept=".dld8,.dld9"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileUpload}
        />

        <Box sx={{ maxWidth: 1100, mx: 'auto', px: 3, py: 3 }}>
          {/* Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 600, color: '#394b57' }}>
              Projects
            </Typography>
          </Stack>

          {/* Search + View toggle */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: '#f1f2f2',
                borderRadius: '6px',
                px: 1.5,
                height: 36,
                boxShadow: '0 -1px 0 0 rgba(0, 0, 0, 0.10), 0 1px 0 0 #FFF',
              }}
            >
              <Search size={15} strokeWidth={2} color="#7d8d97" />
              <InputBase
                placeholder="Search projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ flex: 1, fontSize: 13, color: '#394b57' }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                bgcolor: '#f1f2f2',
                borderRadius: '6px',
                overflow: 'hidden',
                boxShadow: '0 -1px 0 0 rgba(0, 0, 0, 0.10), 0 1px 0 0 #FFF',
              }}
            >
              <ButtonPush
                width="36px"
                height="36px"
                variant={viewMode === 'grid' ? 'active' : 'default'}
                onClick={() => setViewMode('grid')}
              >
                <LayoutGrid size={15} strokeWidth={1.5} />
              </ButtonPush>
              <ButtonPush
                width="36px"
                height="36px"
                variant={viewMode === 'list' ? 'active' : 'default'}
                onClick={() => setViewMode('list')}
              >
                <List size={15} strokeWidth={1.5} />
              </ButtonPush>
            </Box>
          </Stack>

          {/* Filters */}
          <Stack direction="row" spacing={0.75} sx={{ mb: 2 }}>
            {['All', 'Recent', 'Starred'].map((f) => (
              <Box
                key={f}
                onClick={() => setFilter(f.toLowerCase())}
                sx={{
                  px: 1.5,
                  py: 0.4,
                  borderRadius: '12px',
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: 'pointer',
                  bgcolor: filter === f.toLowerCase() ? '#394b57' : '#E9ECED',
                  color: filter === f.toLowerCase() ? '#fff' : '#7d8d97',
                  '&:hover': { bgcolor: filter === f.toLowerCase() ? '#394b57' : '#D8DCDF' },
                }}
              >
                {f}
              </Box>
            ))}
          </Stack>

          {/* Loading */}
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={24} sx={{ color: '#394b57' }} />
            </Box>
          )}

          {/* Project grid/list */}
          {!isLoading && (
            <Box
              sx={{
                display: viewMode === 'grid' ? 'grid' : 'flex',
                gridTemplateColumns:
                  viewMode === 'grid' ? 'repeat(auto-fill, minmax(280px, 1fr))' : undefined,
                flexDirection: viewMode === 'list' ? 'column' : undefined,
                gap: 1.5,
              }}
            >
              {/* + New Project card */}
              <Box
                onClick={() => setShowCreateForm(true)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  p: 2,
                  borderRadius: '12px',
                  border: '2px dashed #C4CDD3',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  minHeight: viewMode === 'list' ? 48 : 160,
                  '&:hover': { borderColor: '#7d8d97', bgcolor: 'rgba(0,0,0,0.02)' },
                }}
              >
                <Plus size={20} strokeWidth={1.5} color="#A9B6BC" />
                <Typography sx={{ color: '#A9B6BC', fontSize: 13, fontWeight: 500 }}>
                  New Project
                </Typography>
              </Box>

              {filteredProjects.map((p) => (
                <ProjectCard
                  key={p.project_id}
                  project={p}
                  onClick={handleOpenProject}
                  viewMode={viewMode}
                  isStarred={starredSet.has(p.project_id)}
                  onToggleStar={toggleStarred}
                />
              ))}
            </Box>
          )}

          {/* Empty message — shown below the grid */}
          {!isLoading && filteredProjects.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4, color: '#7d8d97', fontSize: 14 }}>
              {searchQuery
                ? 'No projects match your search'
                : 'No projects yet. Create your first project!'}
            </Box>
          )}
        </Box>

        {/* Create project dialog */}
        {showCreateForm && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1300,
            }}
            onClick={() => setShowCreateForm(false)}
          >
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{
                width: 500,
                display: 'flex',
                flexDirection: 'column',
                gap: '1px',
                borderRadius: '16px',
                backgroundColor: 'rgba(102, 111, 116, 0.6)',
                boxShadow: '0 24px 24px 0 rgba(67, 79, 86, 0.21)',
              }}
            >
              <HandleTop onIconClick={() => setShowCreateForm(false)} iconAriaLabel="Close" />
              <WindowBackground radius="20px">
                <CardTitle title="New Project">
                  <Stack spacing={1.5}>
                    <InputStd
                      label="Name"
                      value={newProjectName}
                      onChange={(val) => setNewProjectName(val)}
                      align="left"
                    />
                    <InputStd
                      label="Description"
                      value={newProjectDesc}
                      onChange={(val) => setNewProjectDesc(val)}
                      align="left"
                    />
                    <Typography sx={{ color: '#7d8d97', fontSize: 11, textAlign: 'center' }}>
                      A Gitea repository will be created with the default folder structure.
                    </Typography>
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <ButtonStd
                        height="40px"
                        onClick={() => {
                          setShowCreateForm(false);
                          setNewProjectName('');
                          setNewProjectDesc('');
                        }}
                      >
                        Cancel
                      </ButtonStd>
                      <ButtonStd
                        height="40px"
                        onClick={handleCreateProject}
                        disabled={!newProjectName.trim()}
                      >
                        Create Project
                      </ButtonStd>
                    </Stack>
                  </Stack>
                </CardTitle>
              </WindowBackground>
            </Box>
          </Box>
        )}

        {/* Error */}
        {error && (
          <Stack alignItems="center" mt={2}>
            <Box
              sx={{
                bgcolor: '#fce4ec',
                color: '#c62828',
                pl: 3,
                pr: 1,
                py: 1,
                borderRadius: 1,
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <span>{error}</span>
              <Box
                component="button"
                onClick={clearError}
                aria-label="Dismiss error"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 22,
                  height: 22,
                  p: 0,
                  border: 'none',
                  borderRadius: '50%',
                  bgcolor: 'transparent',
                  color: 'inherit',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'rgba(198, 40, 40, 0.12)' },
                }}
              >
                <CloseIcon size={14} strokeWidth={2} />
              </Box>
            </Box>
          </Stack>
        )}
      </>
    );
  }

  // ── PROJECT DETAIL VIEW ────────────────────────────────────────────

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".dld8,.dld9"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 50px)' }}>
        {/* Top bar: breadcrumbs + actions */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            bgcolor: '#E9ECED',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography
              onClick={handleBack}
              sx={{
                fontSize: 13,
                color: '#7d8d97',
                cursor: 'pointer',
                '&:hover': { color: '#394b57' },
              }}
            >
              Projects
            </Typography>
            <ChevronRight size={14} color="#A9B6BC" />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#394b57' }}>
              {currentProject?.name}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            {canSync && (
              <SyncControls syncStatus={syncStatus} syncing={syncing} onSync={handleSync} />
            )}
            {canManageMembers && (
              <ButtonPush
                width="32px"
                height="32px"
                tooltipText="Members"
                onClick={() => setShowMembers(true)}
              >
                <Users size={14} strokeWidth={2} />
              </ButtonPush>
            )}
            <ButtonPush
              width="32px"
              height="32px"
              tooltipText="Settings"
              onClick={() => setShowSettings(true)}
            >
              <Settings size={14} strokeWidth={2} />
            </ButtonPush>
          </Stack>
        </Box>

        {/* Description bar */}
        {currentProject?.description && (
          <Box
            sx={{ px: 2, py: 0.75, borderBottom: '1px solid rgba(0,0,0,0.06)', bgcolor: '#EDEEF1' }}
          >
            <Typography sx={{ color: '#7d8d97', fontSize: 14 }}>
              {currentProject.description}
            </Typography>
          </Box>
        )}

        {/* Main content: explorer + inspector */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          <PanelGroup direction="horizontal" autoSaveId="project-explorer-split">
            <Panel defaultSize={22} minSize={18} maxSize={45}>
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Explorer header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1,
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                  }}
                >
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#394b57' }}>
                    Explorer
                  </Typography>
                  <ButtonStd height="28px" onClick={() => fileInputRef.current?.click()}>
                    Import File
                  </ButtonStd>
                </Box>

                {/* Upload progress */}
                {uploadProgress && (
                  <Box
                    sx={{
                      px: 2,
                      py: 0.5,
                      bgcolor: '#E9ECED',
                      borderBottom: '1px solid rgba(0,0,0,0.06)',
                      fontSize: 12,
                      color: '#394b57',
                    }}
                  >
                    Importing {uploadProgress.name}... {uploadProgress.progress}%
                  </Box>
                )}

                {/* Folder tree */}
                <FolderTree
                  files={projectFiles}
                  selectedFile={selectedFile}
                  onSelectFile={setSelectedFile}
                  onOpenFile={handleOpenFile}
                  isLoading={isLoading}
                  fileStatuses={canShowFileDiff ? syncStatus?.file_statuses || {} : {}}
                />
              </Box>
            </Panel>

            <PanelResizeHandle>
              <Box
                sx={{
                  width: '6px',
                  height: '100%',
                  cursor: 'col-resize',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: '2px',
                    width: '1px',
                    bgcolor: 'rgba(0,0,0,0.08)',
                  },
                  '&:hover::before, &[data-resize-handle-state="hover"]::before, &[data-resize-handle-state="drag"]::before':
                    {
                      bgcolor: 'rgb(156 162 168)',
                      width: '2px',
                    },
                }}
              />
            </PanelResizeHandle>

            <Panel minSize={30}>
              <Box sx={{ height: '100%', display: 'flex' }}>
                <FileInspector
                  file={selectedFile}
                  onOpen={handleOpenFile}
                  onDelete={(f) =>
                    setDeleteConfirm({
                      id: f.filename,
                      name: f.display_name || f.filename,
                      type: 'file',
                    })
                  }
                  onRenamed={(oldFilename, newFilename) => {
                    const updated = useProjectStore
                      .getState()
                      .projectFiles.find((f) => f.filename === newFilename);
                    if (updated) setSelectedFile(updated);
                  }}
                />
              </Box>
            </Panel>
          </PanelGroup>
        </Box>
      </Box>

      {/* Modals */}
      <MembersModal
        open={showMembers}
        onClose={() => setShowMembers(false)}
        members={projectMembers}
        creatorUsername={currentProject?.creator_username}
        currentUsername={currentUsername}
        isOwner={isOwner}
        onAdd={addMember}
        onRemove={removeMember}
        onLeave={useProjectStore.getState().leaveProject}
      />

      <ProjectSettingsModal
        open={showSettings}
        onClose={() => setShowSettings(false)}
        project={currentProject}
        isOwner={isOwner}
        onUpdate={(updates) => updateProject(currentProjectId, updates)}
        onDelete={() =>
          setDeleteConfirm({ id: currentProjectId, name: currentProject?.name, type: 'project' })
        }
      />

      <ConfirmDialog
        open={Boolean(deleteConfirm)}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={
          deleteConfirm?.type === 'project'
            ? handleDeleteProject
            : () => {
                handleDeleteFile({ filename: deleteConfirm?.id });
                setDeleteConfirm(null);
              }
        }
        title={deleteConfirm?.type === 'project' ? 'Delete Project' : 'Delete File'}
        message={
          <Typography variant="body1" sx={{ textAlign: 'center' }}>
            Are you sure you want to delete <strong>{deleteConfirm?.name}</strong>?
            {deleteConfirm?.type === 'project' && ' This will delete all files and data.'}
          </Typography>
        }
        confirmText="Delete"
        cancelText="Cancel"
        width="700px"
      />

      {/* Error */}
      {error && (
        <Stack
          alignItems="center"
          sx={{ position: 'fixed', bottom: 16, left: 0, right: 0, zIndex: 1200 }}
        >
          <Box
            sx={{
              bgcolor: '#fce4ec',
              color: '#c62828',
              pl: 3,
              pr: 1,
              py: 1,
              borderRadius: 1,
              fontSize: 13,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <span>{error}</span>
            <Box
              component="button"
              onClick={clearError}
              aria-label="Dismiss error"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 22,
                height: 22,
                p: 0,
                border: 'none',
                borderRadius: '50%',
                bgcolor: 'transparent',
                color: 'inherit',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(198, 40, 40, 0.12)' },
              }}
            >
              <CloseIcon size={14} strokeWidth={2} />
            </Box>
          </Box>
        </Stack>
      )}
    </>
  );
};

export default ProjectExplorer;
